// Every element a Project Files tree row stands for.
//
// On an index-driven tree (SpaceX BA03, Apple RT06) a Category or Group row
// holds rows that are still "Loading..." placeholders until opened, and
// opening them reads chunked records on a budget - so walking the row gave
// [] and the eye, a click and isolate on it did nothing (owner,
// 2026-09-19). The row's elements then come from the server (POST
// /projects/:id/tree-uids), confined to the row's document - everything
// the model draws for the row, nested parts included; an empty answer
// keeps what the row can see locally.
import { extractLeafUids } from "hooks/useStreamingTreeNode";
import { url, tokenRef } from "api";

// The lazy rows under a node (itself included), and whether any of them
// cannot vouch for all of its elements yet.
export function lazyRowsUnder(node) {
  const out = [];
  const stack = node ? [node] : [];
  while (stack.length) {
    const n = stack.pop();
    if (!n) continue;
    if (n.lazy) { out.push(n); continue; }
    if (Array.isArray(n.nodes)) for (const c of n.nodes) stack.push(c);
  }
  return out;
}

export function rowIsIncomplete(node) {
  return lazyRowsUnder(node).some((n) => !n.lazy.loaded || !Array.isArray(n.allUids));
}

// "S|Category|Group|Name" (recordKey) -> its parts.
export function splitRecordKey(key) {
  const parts = String(key || "").split("|");
  if (parts.length < 4) return null;
  return { section: parts[0], category: parts[1], group: parts[2], name: parts.slice(3).join("|") };
}

// What the server needs to know about a row: its category, and its group
// or component name when the row is one of those. Depth is read off the
// lazy rows: a lazy row is a component; a row whose lazy rows are its
// direct children is a group; anything above is a category.
export function rowQuery(node) {
  const lazies = lazyRowsUnder(node);
  if (!lazies.length) return null;
  const first = splitRecordKey(lazies[0].lazy.key);
  if (!first) return null;
  const expected = lazies.reduce((n, l) => n + (Number(l.lazy.count) || 0), 0);
  const doc = lazies[0].lazy.doc;
  if (node.lazy) return { ...first, doc, expected };
  const directChildrenLazy = Array.isArray(node.nodes) && node.nodes.some((c) => c && c.lazy);
  if (directChildrenLazy) return { section: first.section, category: first.category, group: first.group, doc, expected };
  return { section: first.section, category: first.category, doc, expected };
}

// The compact answer: each document prefix once, then its elements' ids.
export function expandCompact(body) {
  if (Array.isArray(body.uids)) return body.uids;
  const out = [];
  const byPrefix = body.byPrefix || {};
  Object.keys(byPrefix).forEach((prefix) => {
    String(byPrefix[prefix] || "").split(",").forEach((id) => { if (id) out.push(`${prefix}-${id}`); });
  });
  (body.other || []).forEach((u) => out.push(u));
  return out;
}

const auth = () => ({ Authorization: `Bearer ${localStorage.getItem(tokenRef)}` });

// A category that the same file lists under more than one phase section
// (New and Existing - Apple RT06's MP model: Duct Fittings 2,965 + 2,329):
// the model's element rows carry no phase, so a server list would mix them.
// Such a row is answered from its own records instead.
export function categoryInSeveralSections(roots, doc, category) {
  const sections = new Set();
  const stack = [...(roots || [])];
  while (stack.length) {
    const n = stack.pop();
    if (!n) continue;
    if (n.lazy) {
      if (n.lazy.doc === doc) {
        const k = splitRecordKey(n.lazy.key);
        if (k && k.category === category) sections.add(k.section);
      }
      continue;
    }
    if (Array.isArray(n.nodes)) for (const c of n.nodes) stack.push(c);
  }
  return sections.size > 1;
}

// Open every lazy row under a node, a few at a time, within a budget.
async function loadRowsUnder(node, loadLazy, { maxElements = 60000 } = {}) {
  const rows = lazyRowsUnder(node).filter((n) => !n.lazy.loaded);
  const total = rows.reduce((a, n) => a + (Number(n.lazy.count) || 0), 0);
  if (total > maxElements) return false;
  for (let i = 0; i < rows.length; i += 4) {
    // eslint-disable-next-line no-await-in-loop
    await Promise.all(rows.slice(i, i + 4).map((n) => loadLazy(n).catch(() => null)));
  }
  return true;
}

// -> { uids, source: 'local' | 'server', complete }
export async function resolveRowUids(projectId, node, { prefixesOf = () => [], fetcher = fetch, roots = null, loadLazy = null } = {}) {
  const local = extractLeafUids(node);
  if (!projectId || !rowIsIncomplete(node)) return { uids: local, source: "local", complete: true };
  const q = rowQuery(node);
  if (!q) return { uids: local, source: "local", complete: false };
  if (roots && loadLazy && categoryInSeveralSections(roots, q.doc, q.category)) {
    const opened = await loadRowsUnder(node, loadLazy);
    const uids = extractLeafUids(node);
    return { uids, source: "records", complete: opened && !rowIsIncomplete(node), sectioned: true };
  }
  try {
    const res = await fetcher(`${url}/projects/${projectId}/tree-uids`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...auth() },
      body: JSON.stringify({ prefixes: prefixesOf(q.doc) || [], category: q.category, group: q.group, name: q.name, expected: q.expected }),
    });
    const body = res && res.ok ? await res.json() : null;
    // Too many to act on from the tree (SpaceX Electrical Fixtures: over a
    // million): said, never half done.
    if (body && body.tooMany) return { uids: [], source: "server", complete: false, tooMany: true, count: body.count, limit: body.limit };
    // Not held to the row's own count: the model's element rows include
    // nested family instances the record tree does not list (SpaceX host,
    // Conduit Fittings: 4,258 in the tree, 35,423 drawn), and hiding or
    // isolating a category means all of what is drawn for it.
    const uids = body ? expandCompact(body) : [];
    if (uids.length) return { uids, source: "server", complete: true, count: uids.length };
  } catch (e) { /* keep what the row can see */ }
  return { uids: local, source: "local", complete: false };
}
